import React from "react";
import { motion } from "framer-motion";
import { FileText, Sparkles } from "lucide-react";

/**
 * DocumentScanner — animated visualization of a document being scanned
 * with extracted fields populating one-by-one. Used in Hero.
 */
const fields = [
  { label: "Sender", value: "ACME Logistics LLC", delay: 1.2 },
  { label: "Amount", value: "$4,280.00", delay: 1.9 },
  { label: "Deadline", value: "Mar 14, 2026", delay: 2.6 },
  { label: "Type", value: "Rate Confirmation", delay: 3.3 },
];

export const DocumentScanner = () => {
  return (
    <div className="relative w-full h-full flex items-center justify-center" data-testid="document-scanner">
      {/* Document */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative w-[290px] sm:w-[330px] h-[400px] rounded-2xl border overflow-hidden"
        style={{
          backgroundColor: "var(--surface)",
          borderColor: "var(--border-strong)",
          boxShadow: "0 30px 80px -20px rgba(123,47,190,0.35)",
        }}
      >
        {/* paper shine */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "linear-gradient(180deg, rgba(123,47,190,0.06), transparent 30%)",
          }}
        />

        {/* doc header */}
        <div className="px-5 pt-5 flex items-center gap-2">
          <FileText size={14} className="text-[#7B2FBE]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--text-subtle)" }}>
            Document.pdf
          </span>
        </div>

        {/* doc body lines */}
        <div className="p-5 space-y-2.5 mt-2">
          {[88, 65, 92, 70, 80, 55, 95, 60, 85, 72, 50, 88].map((w, i) => (
            <motion.div
              key={i}
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 0.45 }}
              transition={{ duration: 0.45, delay: 0.4 + i * 0.05 }}
              className="h-1.5 rounded-full origin-left"
              style={{
                width: `${w}%`,
                backgroundColor: i % 4 === 0 ? "rgba(123,47,190,0.45)" : "var(--text-subtle)",
              }}
            />
          ))}
        </div>

        {/* scan line */}
        <motion.div
          className="absolute left-0 right-0 h-[2px] pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent, #2DD4BF, #7B2FBE, transparent)",
            boxShadow: "0 0 18px rgba(45,212,191,0.7)",
          }}
          initial={{ y: 0 }}
          animate={{ y: [0, 380, 0] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      {/* Extracted data card (right side, slight offset) */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        className="absolute -right-2 sm:-right-6 bottom-8 w-[230px] sm:w-[260px] rounded-xl border backdrop-blur-xl"
        style={{
          backgroundColor: "color-mix(in srgb, var(--surface) 88%, transparent)",
          borderColor: "var(--border-strong)",
          boxShadow: "0 20px 50px -10px rgba(45,212,191,0.25)",
        }}
      >
        <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b" style={{ borderColor: "var(--border)" }}>
          <Sparkles size={12} className="text-[#2DD4BF]" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--text-subtle)" }}>
            Extracted
          </span>
          <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-[#2DD4BF]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4BF] animate-pulse" /> live
          </span>
        </div>
        <div className="p-4 space-y-2.5">
          {fields.map((f) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: f.delay }}
              className="flex items-baseline justify-between gap-3"
            >
              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--text-subtle)" }}>
                {f.label}
              </span>
              <span className="text-sm font-medium font-mono truncate" style={{ color: "var(--text)" }}>
                {f.value}
              </span>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 4 }}
            className="pt-2 mt-1 border-t flex items-center gap-2 text-[10px]"
            style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2DD4BF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13l4 4L19 7" />
            </svg>
            Logged to dashboard · 2.4s
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default DocumentScanner;
